import { Box, Button } from '@mui/material';
import { BoxProps } from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import React from 'react';

// Page Container
export const PageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(0.5),
  },
}));

// Header section containing title and action buttons
export const PageHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(1),
  marginRight: theme.spacing(1),
  alignItems: 'center',
  justifyContent: 'space-between',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginTop: theme.spacing(2),
  },
}));

// Title section with fixed width
export const TitleSection: React.ComponentType<BoxProps> = styled(Box)(() => ({
  flex: '0 0 25%',
}));

// Action button container for multiple buttons
export const ActionButtonContainer = styled(Box)(({ theme }) => ({
  marginLeft: 'auto',
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

// Register button - consistent with companies and properties pages
export const CreateButton = styled(Button)(({ theme }) => ({
  minWidth: '50%',
  height: '40px',
  textTransform: 'none',
  borderRadius: theme.spacing(3),
  fontWeight: 600,
  marginTop: theme.spacing(0.5),
  [theme.breakpoints.down('sm')]: {
    height: '40px',
  },
}));

// Status constants for better maintainability
export const ORDER_STATUS = {
  UNSPECIFIED: 1, // 指定なし
  NEGOTIATING: 2, // 交渉中
  ARRANGED: 3, // 手配済
} as const;

// Status background colors for row
export const getStatusColors = (isPreconfirmed: boolean) => ({
  [ORDER_STATUS.UNSPECIFIED]: '#FFFFFF', // White
  [ORDER_STATUS.NEGOTIATING]: '#FFE5E5', // Red (High Priority)
  [ORDER_STATUS.ARRANGED]: isPreconfirmed ? '#E5F0FF' : '#FFFFFF', // Blue (Active)
});

export const STATUS_TEXT = {
  [ORDER_STATUS.UNSPECIFIED]: '指定なし',
  [ORDER_STATUS.NEGOTIATING]: '交渉中',
  [ORDER_STATUS.ARRANGED]: '手配済',
} as const;

// Helper function to get common style properties
const getCommonRowStyle = (borderColor: string, isPreconfirmed: boolean) => ({
  borderLeft: `4px solid ${borderColor}`,
  fontWeight: !isPreconfirmed ? 700 : undefined,
});

// Helper function to get archive-related styles
const getArchiveStyles = (isArchive: boolean) => ({
  opacity: isArchive ? 0.6 : 1,
  color: isArchive ? '#bdbdbd' : undefined,
});

// Row style helpers for PlanTable
export const getPlanRowStyle = ({
  isSelected,
  isPast,
  isArchive,
  isPreconfirmed,
  borderColor,
  statusBgColor,
}: {
  isSelected: boolean;
  isPast: boolean;
  isArchive: boolean;
  isPreconfirmed: boolean;
  borderColor: string;
  statusBgColor: string;
}) => {
  const commonStyle = getCommonRowStyle(borderColor, isPreconfirmed);
  const archiveStyles = getArchiveStyles(isArchive);

  // Priority: Selected rows > Past date > Archive > Status background
  if (isSelected) {
    return {
      backgroundColor: '#D7E2FF',
      ...archiveStyles,
      ...commonStyle,
    };
  }
  if (isPast) {
    return {
      backgroundColor: '#f5f5f5',
      opacity: isArchive ? 0.6 : 1,
      color: isArchive ? '#000' : undefined,
      ...commonStyle,
    };
  }
  if (isArchive) {
    return {
      opacity: 0.6,
      color: '#bdbdbd',
      backgroundColor: '#f5f5f5',
      ...commonStyle,
    };
  }
  return {
    backgroundColor: statusBgColor,
    ...commonStyle,
    ...archiveStyles,
  };
};
